﻿/*****
    Author:	    CT Software Systems, LLC
    Last Modified: 260420
*/
'use strict';

import {
    getPropNmAndKey,
    getTempKey
} from './ctssArrayUtils.js'; 
import {
    boldOnClick,
    convertATagsToSpans,
    convertSpansToATags,
    init_ctssEditor,
    italicOnClick,
    showActiveFormatBtns,
    underlineOnClick
} from './ctssEditor.js';
import {
    isValidLink
} from './ctssFetch.js';
import {
    cleanAndPasteText
} from './ctssFormat.js';
import {
    getMoveIcon
} from './ctssHtml.js';
import {
    deleteImgFromCloudAsync,
    hasImgsToDelete,
    hasImgsToUpload,
    isValidImgAsync,
    setHasImgsToDelete,
    setHasImgsToUpload,
    uploadImgToCloudAsync,
    uploadToImgCtrl
} from './ctssImg.js';
import {
    allSettled,
    deletePromise,
    getPromise,
    getSortKeysFromTableOrder,
    initDragDropTableSort,
    savePromise,
    showMySNError
} from './ctssJQuery.js';
import {
    isDataModified,
    missingRequiredData,
    setModified
} from './ctssVal.js';
import {
    setGroupsDDLContent
} from './snGroupCtrls.js';
import {
    initPinnedImgsAsync,
} from './snPinnedImages.js';
import {
    baseURL,
    Features,
    ImgsFolder,
    appDebugMode,
    getAppToken,
    getImgsBaseURLAsync,
    getTestFolderParams,
    setMenu
} from './snSite.js';

/* private functions */


const { useTestFolder, testFolderParam } = getTestFolderParams();

const token = getAppToken();

const addBlankLineAsync = async (params = {}) => {
    const sermonDtl = {
        id: getTempKey({ arr: sermonDtls, fldNm: 'id' }),
        isBlankLine: true,
        displayOrder: getInitialDisplayOrder()
    };
    sermonDtls.push(sermonDtl);
    $('#gridNotes>tbody').append(await getSermonDtlRowAsync({ sermonDtl }));
    setModified(true);
};

const addImgAsync = async (params = {}) => {

    if (!params.imgSrc) {
        alert('ImgSrc is a required parameter for addImgAsync!');
        return;
    }

    const id = getTempKey({ arr: sermonDtls, fldNm: 'id' });
    const sermonDtl = {
        id: id,
        content: $('#hiddenImgID').val(),
        displayOrder: getInitialDisplayOrder(),
        isImage: true,
        isImgToUpload: true,
        imgSrc: params.imgSrc
    };
    sermonDtls.push(sermonDtl);
    /* tbody of gridnotes - not icon toolbar so use > for immediate descendant */
    $('#gridNotes>tbody').append(await getSermonDtlRowAsync({ sermonDtl: sermonDtl }));

    setModified(true);
}

const addNoteAsync = async (params = {}) => {
    const sermonDtl = {
        id: getTempKey({ arr: sermonDtls, fldNm: 'id' }),
        content: params.content === undefined ? 'Enter your note here...' : params.content,
        hasComment: true,
        displayOrder: getInitialDisplayOrder()
    };
    sermonDtls.push(sermonDtl);
    $('#gridNotes>tbody').append(await getSermonDtlRowAsync({ sermonDtl: sermonDtl }));
    setModified(true);

    if (params.content === undefined) {
        $('#note_' + sermonDtl.id)
            .on('dragstart', e => e.preventDefault())
            .on('input', () => setModified(true))
            .on('keyup mouseup select', showActiveFormatBtns)
            .on('keypress', e => { if (e.keyCode === 13) e.preventDefault(); })
            .on('paste', cleanAndPasteText)
            .focus();
    }
    showActiveFormatBtns();
};

const deleteNote = (params = {}) => {
    let { key } = params;
    key = parseInt(key, 10);
    const idx = sermonDtls.findIndex(x => x.id === key);
    if (idx > -1 && sermonDtls[idx].isImage && sermonDtls[idx].id > 0) {

        /* Image was saved previously - hide row and add deletedImg class to img    */
        /* giving the appearance that the image was deleted.  The imgToDelete class  */
        /* is used to locate and actually delete the image if the user saves their  */
        /* changes                                                                  */

        /* Cache selector so jQuery doesn't have to search DOM again */
        const $noteRow = $(`#${key}`);
        $noteRow
            .addClass('hidden')
            .find('img').addClass('imgToDelete');

        setHasImgsToDelete(true);
        setModified(true);
    } else {
        removeNoteFromGrid(params);
    }
}

const formatRptData = (params = {}) => {
    const { sermonDtls } = params;
    if (!sermonDtls) {
        alert('sermonDtls is a required parameter for snSermonsAdd.formatRptData!');
        return;
    }
    const formattedRptData = structuredClone(sermonDtls);
    formattedRptData.map(x => {
        if (x.isImage === true) {
            x.content = x.imgSrc
        }
        else if (x.isBlankLine === true) {
            x.content = '';
        }
    });
    return formattedRptData;
};

const getCompletedCountAsync = async () =>
    await getPromise({ url: `Sermons/${token.CG}/${sermonID}/CompletedCount` });

const getInitialDisplayOrder = () => sermonDtls.length > 0 ? Math.max(...sermonDtls.map(x => x.displayOrder)) + 1 : 1;      

const getSermonDataAsync = async (params = {}) => {
    const { sermonID } = params;
    if (!sermonID || sermonID === -1) {
        alert('SermonID is a required parameter for snSermonsAdd.getSermonDataAsync!'); 
        return;
    }
    return await getPromise({ url: `Sermons/Draft/${token.CG}/${sermonID}` })
}

const getSermonDtlRowAsync = async (params = {}) => {

    const {
        id,
        content,
        displayOrder,
        hasComment,
        imgSrc,
        isBlankLine,
        isImage,
        isImgToUpload
    } = params.sermonDtl

    let row = `<tr id="${id}" data-displayOrder="${displayOrder}" >` +
        '<td colspan="2" class="col-md-11">';
    if (isBlankLine) {
        row += '<div class="blankLine" >A BLANK LINE</div>';
    } else if (isImage) {

        const imgClass = isImgToUpload
            ? 'img-responsive imgToUpload sermonImg'
            : 'img-responsive sermonImg';

        row += `<div class="w300"><img id="img_${id}" src="${imgSrc}" class="${imgClass}" data-img_name="${content}"/></div>`;

    } else {
        /* id is added so that event listeners can be added for a new note - see addSermonNote */
        row += `<div id="note_${id}" contenteditable="true" class="note" >${content}</div>`;
    }

    let icons = '<table class="h-icon-toolbar"><tbody><tr>';
    if (!isBlankLine && !isImage) {
        const commentIcon = hasComment ? 'fa-check-square-o' : 'fa-square-o';
        icons += `<td><div><span class="fa ${commentIcon}" title="Include comment at end of note"` +
            ` data-action="toggle_comment"></span></div></td>`;
    } else {
        icons += '<td><div></div></td>';
    }
    icons += '<td><div><span class="fa fa-trash" data-toggle="modal" data-target="#deleteDlg"' +
        ` data-key="${id}"></span></div></td>`;
    icons += `<td><div>${getMoveIcon()}</div></td>`;

    icons += '</tr></tbody></table>';

    row += '</td><td class="col-md-1">' + icons + '</td></tr>';

    return row;
}

// Optimized ggetSermonDtlRowAsyncAsync function
const getSermonDtlRowsAsync = async() => {
    // Build all rows in a single string and update DOM once

    const rows = await Promise.all(
        sermonDtls.map(sermonDtl => getSermonDtlRowAsync({ sermonDtl }))
    );
    $('#gridNotes>tbody').html(rows.join(''));


    // Cache selector for editable notes
    const $editableNotes = $('div[contenteditable="true"]');
    $editableNotes
        .on('dragstart', e => e.preventDefault())
        .on('input', () => setModified(true))
        .on('keyup mouseup select', showActiveFormatBtns)
        .on('keypress', e => { if (e.keyCode === 13) e.preventDefault(); })
        .on('paste', cleanAndPasteText);
}

const getSermonTags = () => {

    const editTags = $('#editTags');
    tags.map(x => editTags.tagsinput('add', x.tag));
}

const initDeleteDlg = (params) => {
    $('#deleteDlg').on('show.bs.modal', (e) => {

        const key = e.relatedTarget.dataset.key;
        const noteObj = sermonDtls.find(x => x.id === parseInt(key, 10));

        let prompt = params.usedMsg ?? '';
        if (key === undefined) { /* Deleting entire sermon */
            if (prompt.length > 0) {
                prompt += 'Deleting this sermon will remove it from the ' +
                    'new sermons list on member phones. Members who have ' +
                    'already completed the sermon will still continue to see ' +
                    'the notes that they have taken for this sermon. ';
            }
            prompt += 'Are you sure you want to delete this sermon?';
            $('#deleteHdr').text('Deleting sermon...');
            $('#deletePrompt').text(prompt);
        } else {
            /* Deleting a note, blank line or image on sermon */

            if (noteObj?.isBlankLine) {
                prompt = "Are you sure you want to delete this blank line?";
                $('#deleteHdr').text('Deleting blank line...');
            } else if (noteObj?.isImage) {
                prompt = "Are you sure you want to delete this image?";
                $('#deleteHdr').text('Deleting image...');
            } else {
                if (prompt.length > 0) {
                    prompt += 'Deleting this note changes the content of this sermon ' +
                        'once it is saved. This newly changed sermon will be available to ' +
                        'church members who have not completed this sermon.  Members who have ' +
                        'already completed the sermon will continue to see their version of ' +
                        'the sermon.  We do this to ensure that a church member\'s notes ' +
                        'are not changed after they have completed them. ';
                }
                prompt += 'Are you sure you want to delete this note?';
                $('#deleteHdr').text('Deleting note...');
            }
            $('#deletePrompt').text(prompt);
        }

        $('#btnDelete').off('click').click(
            async () => {
                if (key === undefined) { /* Deleting entire sermon */
                    if (sermonID === -1) {
                        /* Sermon never saved - just return to edit page */
                        setModified(false);
                        window.location.href = "../Sermons/Edit";
                    }
                    else {
                        await deletePromise({
                            url: `Sermons/Sermon/${token.CG}/${sermonID}${testFolderParam}`,
                            divErrMsg: 'divDelDlgErrMsg'
                        }).then(() => { window.location.href = "../Sermons/Edit"; });
                    }
                } else {
                    deleteNote({ key: key });
                }
                $('#deleteDlg').modal('hide');
            });
    });
}

const initPrintDlg = () => {
    $('#printDlg').on('show.bs.modal', async (e) => {

        $('#btnPrint').off('click').on('click', async () => {
            let lines = $('input[name="rptLines"]:checked').val();
            if (lines === "customLines") {
                lines = $('#customLines').val();
            }

            if (isDataModified() === true) {
                await saveSermonAsync({ btnId: 'btnPrint' });
                const sermonData = await getSermonDataAsync({ sermonID });
                sermonDtls = sermonData.sermonDtls;
            }

            let groupName = $('#divGroupDDL > button').text().trim();
            let groupImgName = $('#divGroupDDL > button > img').attr('src');
            if (!groupName) {
                groupName = $('#grpDesc').text().trim();
                groupImgName = $('#grpImg').attr('src');
            }

            sessionStorage.setItem('baseURL', baseURL);
            sessionStorage.setItem('church', token.Church);
            sessionStorage.setItem('groupID', $('#hiddenGroupID').val());
            sessionStorage.setItem('groupImgName', groupImgName);
            sessionStorage.setItem('groupName', groupName);

            const draftData = formatRptData({ sermonDtls });

            const noteObjects = await savePromise({
                url: `Sermons/FormatDraftSermonDtlForRpt/${lines}`,
                data: draftData
            });

            sessionStorage.setItem('noteObjects', JSON.stringify(noteObjects));
            sessionStorage.setItem('subTitle', $('#editSubTitle').val());

            const title = $('#editTitle').val();
            sessionStorage.setItem('title', title);

            const url = `../Reports/SermonDraft?title=${title}`;
            window.open(url, '_blank');
            $('#printDlg').modal('hide');
        });
    });
}

const initSaveDlg = (params) => {
    $('#saveDlg').on('show.bs.modal', (e) => {
        const prompt = (params.usedMsg ?? '') +
            'The changes you make to the sermon will be available ' +
            'to church members who have not completed this sermon. Members ' +
            'who have already completed the sermon will continue to see the ' +
            'previous version. This ensures that a church member\'s ' +
            'notes are not changed after they complete them.  Proceed with ' +
            'saving changes?';
        $('#savePrompt').text(prompt);
    });
}

const initSermonTags = () => {
    const editTags = $('#editTags');

    editTags.tagsinput('add', ''); /* Initialize control - else 1st tag is never displayed */

    editTags.on('itemAdded', (event) => {
        if (tags.findIndex(x => x.tag === event.item) === -1) {
            tags.push({ id: -1, tag: event.item });
        }
        setModified(true);
    });
    editTags.on('itemRemoved', (event) => {
        tags.splice(tags.findIndex(x => x.tag === event.item), 1);
        setModified(true);
    });
}

const refreshSermonElementsAsync = async (params = {}) => {

    const {
        sermon 
    } = params;   

    convertATagsToSpans({ arr: sermon.sermonDtls, fldNm: 'content' });

    // convert from mm/dd/yyyy to ISO format yyyy-mm-dd:Thh:mm:ss.000Z 
    // remove time portion of string -- resulting in yyyy-mm-dd 

    let dateGiven = new Date(sermon.dateGiven).toISOString();
    dateGiven = dateGiven.substring(0, dateGiven.indexOf('T'));
    $('#hiddenGroupID').val(sermon.groupID);
    $('#editTitle').val(sermon.title);
    $('#editSubTitle').val(sermon.subTitle);
    $('#editDateGiven').val(dateGiven);
    $('#editVideoURL').val(sermon.videoURL);

    sermonDtls = sermon.sermonDtls;
    tags = sermon.sermonTags;
    await getSermonDtlRowsAsync();
    getSermonTags();

}

const removeNoteFromGrid = (params) => {
    sermonDtls.splice(sermonDtls.findIndex(x => x.id === parseInt(params.key, 10)), 1);
    $('#' + params.key).remove();
    setModified(true);
    showActiveFormatBtns();
}

const saveSermonAsync = async (params = {}) => {

    if (missingRequiredData({
        dataItems: [
            { ctrlNm: '#editTitle', labelText: 'Title' },
            { ctrlNm: '#editDateGiven', labelText: 'Date Given' },
        ]
    })) return;
    $('#divErrMsg').addClass('hidden');

    /* Update array for changes entered into contenteditable divs */
    $('.note').each(function () {
        const key = getPropNmAndKey(this.id).key;
        const idx = sermonDtls.findIndex(x => x.id === key);
        sermonDtls[idx].content =
            $('#' + this.id).html()
                .replace(/&nbsp;/g, " ")
                .replace(/<[b]>[\s]+<\/[b]>|<[i]>[\s]+<\/[i]>/g, "")/* <b> </b> or <i> </i>*/
                .replace(/<b><\/b>|<i><\/i>/g, "") /* <b></b> or <i></i>*/
                .replace(/<b><b>/g, "<b>") /* <b><b> with <b> */
                .replace(/<\/b><\/b>/g, "</b>") /* </b></b> with </b> */
                .replace(/<i><i>/g, "<i>") /* <i><i> with <i> */
                .replace(/<\/i><\/i>/g, "</i>"); /* </i></i> with </i> */
    });

    convertSpansToATags({ arr: sermonDtls, fldNm: 'content' });

    if (hasImgsToDelete() || hasImgsToUpload()) { // Save after deleting or uploading images
        const imgPromises = [];
        if (hasImgsToDelete()) {
            const imgsToDelete = document.querySelectorAll('.imgToDelete');
            imgsToDelete.forEach(img => {

                const id = parseInt(img.closest('tr').id,10);
                const idx = sermonDtls.findIndex(x => x.id === id);

                imgPromises.push(deleteImgFromCloudAsync({
                    imgsFolder: ImgsFolder.SermonNotes,
                    useTestFolder: useTestFolder,
                    imgName: sermonDtls[idx].content
                }));

                // remove from sermonDtls array and html

                sermonDtls.splice(idx, 1);
                $(`#${id}`).remove();

            });
        }
        if (hasImgsToUpload()) {
            const imgsToUpload = document.querySelectorAll('.imgToUpload');
            imgsToUpload.forEach(img => {

                const id = parseInt(img.closest('tr').id,10);
                const idx = sermonDtls.findIndex(x => x.id === id);
                if (idx === -1) return;
               
                imgPromises.push(uploadImgToCloudAsync({
                    imgsFolder: ImgsFolder.SermonNotes,
                    useTestFolder: useTestFolder,
                    imgCtrl: `#img_${sermonDtls[idx].id}`,
                    url: 'Storage/Img',
                    resizeImage: true,
                    useAspectRatio: true,
                    btnId: '#btnSave',
                    divErrMsg: 'divUpdDlgErrMsg',
                    postOnload: async (imgNm) => {
                        sermonDtls[idx].content = imgNm;
                        $(`#${sermonDtls[idx].id}`)
                            .replaceWith(await getSermonDtlRowAsync({ sermonDtl: sermonDtls[idx] }));
                    }
                }));
            });
        }

        await Promise.all(imgPromises).then(async () => {
            await saveSermonDataAsync(params);
        });
    } else {
        await saveSermonDataAsync(params);
    };
};

const saveSermonDataAsync = async (params = {}) => {
    const btnId = params.data === undefined ? params.btnId : params.data.btnId;
    sermonID = await savePromise({
        url: 'Sermons/Sermon',
        data: {
            id: sermonID,
            groupID: $('#hiddenGroupID').val(),
            title: $('#editTitle').val(),
            subTitle: $('#editSubTitle').val(),
            dateGiven: $('#editDateGiven').val(),
            videoURL: $('#editVideoURL').val(),
            sermonDtls: sermonDtls,
            sermonTags: tags
        },
        btnId: btnId
    }).then((sid) => {
        sessionStorage.setItem('sid', sid);
        return sid;
    });
}

const toggleComment = (params = {}) => {
    const key = parseInt(params.key, 10);
    const idx = sermonDtls.findIndex(x => x.id === key);
    if (idx === -1) return;

    const sermonDtl = sermonDtls[idx];
    sermonDtl.hasComment = !sermonDtl.hasComment;
    sermonDtl.content = $(`#note_${key}`).html();

    const $newRow = $(getSermonDtlRow({ sermonDtl }));
    $(`#${key}`).replaceWith($newRow);

    // Only re-attach events if the note is not blank line or image
    if (!sermonDtl.isBlankLine && !sermonDtl.isImage) {
        $newRow.find(`#note_${key}`)
            .on('dragstart', e => e.preventDefault())
            .on('input', () => setModified(true))
            .on('keyup mouseup select', showActiveFormatBtns)
            .on('keypress', e => { if (e.keyCode === 13) e.preventDefault(); })
            .on('paste', cleanAndPasteText);
    }

    setModified(true);
};

/* public variables and functions */

let groupImgsBaseURL = '';
let sermonID = -1;
let sermonImgsBaseURL = '';
let sermonDtls = [];
let tags = [];
let testFolderImgsBaseURL = '';

const getSermonRptDataAsync = async (params) => {

    const [rData, sImgsBaseURL, tImgsBaseURL] = await allSettled({
        promises: [
            await getPromise({
                url: `Sermons/SermonRpt/${params.sid}/${params.cg}/${params.lines}`
            }),
            getImgsBaseURLAsync(ImgsFolder.SermonNotes),
            getImgsBaseURLAsync(ImgsFolder.TestFolder)
        ]
    })
    if (rData.status === 'rejected' ||
        sImgsBaseURL.status === 'rejected' ||
        tImgsBaseURL.status === 'rejected') {
        showMySNError({ errMsg: 'Unable to retrieve report data. Please contact support@mysermonnotes.com...' })
        return;
    }

    const sermonImgsBaseURL = sImgsBaseURL.value;
    const testFolderImgsBaseURL = tImgsBaseURL.value;
    const data = rData.value;
    console.log('report data', data);

    const noteObjects = data.noteObjects;

    // Wait for all async operations in noteObjects
    await Promise.all(noteObjects.map(async (x) => {
        if (x.isImage === true) {
            if (!await isValidLink({ url: x.imgSrc })) {
                const imgName = x.imgSrc.substring(x.imgSrc.lastIndexOf('/') + 1);
                x.imgSrc = testFolderImgsBaseURL + imgName;
            }
        }
    }));

    return data;
}

const initAsync = async () => {
    window.onbeforeunload = () => {
        if (isDataModified() === true) {
            return "Your changes have not been saved...";
        }
        sessionStorage.removeItem('sid');
    };
    setMenu();
    init_ctssEditor({
        activeBtnClass: 'editorActiveBtns',
        useColorPicker: true,
        useHyperlinks: true
    });

    let groupID = sessionStorage.getItem('groupID') === 'undefined'
        ? undefined
        : sessionStorage.getItem('groupID');

    groupImgsBaseURL = sessionStorage.getItem('groupImgsBaseURL');
    testFolderImgsBaseURL = sessionStorage.getItem('testFolderImgsBaseURL');

    setGroupsDDLContent({
        ddlListItems: JSON.parse(sessionStorage.getItem('groups')),
        groupID,
        groupImgsBaseURL,
        testFolderImgsBaseURL
    });

    sessionStorage.removeItem('groups');
    sessionStorage.removeItem('groupImgsBaseURL');
    sessionStorage.removeItem('groupID');
    sessionStorage.removeItem('testFolderImgsBaseURL');
   

    sermonID = sessionStorage.getItem('sid');
    if (sermonID === null) {
        sermonID = -1; // Adding a new sermon
        $('#loader').css('display', 'none');
        await addNoteAsync({});
        initDeleteDlg({});
        initSaveDlg({});
        $('#btnDeleteSermon').removeClass('hidden');
        $('#btnSave').on('click', () => saveSermonAsync({ btnId: 'btnSave' }));
        setModified(false);

        const [_,sImgsBaseURL,tImgsBaseURL] = await allSettled({
            promises: [
                initPinnedImgsAsync({
                    entityNm: 'sermon',
                    featureID: Features.Sermons,
                    fnPostImgAdd : addImgAsync
                }),
                getImgsBaseURLAsync(ImgsFolder.SermonNotes),
                getImgsBaseURLAsync(ImgsFolder.TestFolder)
            ]
        });

        if (sImgsBaseURL.status !== 'rejected') {
            sermonImgsBaseURL = sImgsBaseURL.value;
        }
        if (tImgsBaseURL.status !== 'rejected') {
            testFolderImgsBaseURL = tImgsBaseURL.value;
        }
        initPrintDlg();
    } else {
        const [_,completedSermonCount, sermonData, sImgsBaseURL, tImgsBaseURL] = await allSettled({
            promises: [
                initPinnedImgsAsync({
                    entityNm: 'sermon',
                    featureID: Features.Sermons,
                    fnPostImgAdd: addImgAsync
                }),
                getCompletedCountAsync(),
                getSermonDataAsync({ sermonID }),
                getImgsBaseURLAsync(ImgsFolder.SermonNotes),
                getImgsBaseURLAsync(ImgsFolder.TestFolder)
            ]
        });

        // Process all promise results first
        if (completedSermonCount.status !== 'rejected') {
            const completedCount = completedSermonCount.value;
            const usedMsg = completedCount === 0
                ? ''
                : completedCount === 1
                    ? 'This sermon has already been completed by a member of your church. '
                    : `This sermon has already been completed by ${completedCount} members of your church. `;

            if (completedCount > 0) {
                $('#lblCompletedCount').text(`Sermon Completed: ${completedCount} times..`);

                $('#btnSave').on('click', () => $('#saveDlg').modal('show'));

                $('#btnSaveDlg').on('click', async () => {
                    await saveSermonAsync({ btnId: 'btnSaveDlg' });
                    $('#saveDlg').modal('hide');
                });
            } else {
                $('#btnSave').on('click', async () => await saveSermonAsync({ btnId: 'btnSave' }));
            }

            initDeleteDlg({ usedMsg });
            initSaveDlg({ usedMsg });
        }

        // Set image base URLs before calling refreshSermonElementsAsync
        if (sImgsBaseURL.status !== 'rejected') {
            sermonImgsBaseURL = sImgsBaseURL.value;
        }
        if (tImgsBaseURL.status !== 'rejected') {
            testFolderImgsBaseURL = tImgsBaseURL.value;
        }

        // Now refresh sermon elements after all dependencies are set
        if (sermonData.status !== 'rejected') {
            initSermonTags();
            await refreshSermonElementsAsync({ sermon: sermonData.value });
            setModified(false);
        }

        initPrintDlg();
    }

    initDragDropTableSort({
        selector: '#gridNotes > tbody',
        fnStop: () => {
            const sorted = getSortKeysFromTableOrder({ selector: '#gridNotes > tbody > tr' });
            sorted.map(item =>
                sermonDtls[sermonDtls.findIndex(x => x.id === parseInt(item.id, 10))].displayOrder = item.displayOrder);
            sermonDtls.sort((a, b) => a.displayOrder - b.displayOrder);

            /* Do not save the new order to the database until the user clicks Save     */
            /* which saves changes to the sermon as a whole which allows user to cancel */
            /* any changes including reordering made since the last save.               */

            setModified(true);
        }
    });

    $('#btnAddNote').on('click', addNoteAsync);
    $('#btnBlankLine').on('click', addBlankLineAsync);
    $('#btnUnderline').on('click', underlineOnClick);
    $('#btnBold').on('click', boldOnClick);
    $('#btnItalic').on('click', italicOnClick);

    $('#gridNotes tbody').on('click', 'span[data-action="toggle_comment"]',
        (e) => {
            const id = e.target.closest('tr').id;
            toggleComment({ key: id });
        }) 
};

export {
    formatRptData,
    getSermonDataAsync
}

window.snSermonsAdd = {
    getSermonRptDataAsync,
    initAsync
};
